import { useId, useMemo } from "react";
import { useLocale, useCurrency } from "../../intl";
import { localeToUrlSegment } from "../../intl/manifestI18n";
import { getI18nRollout } from "../../intl/i18nRollout.js";
import { writeClientViewLocaleCookie, writeClientViewCurrencyCookie } from "../../intl/clientViewPreferences.js";
import { AUTOMAP_PARTNER_PORTAL_I18N_MANIFEST } from "../../constants/automapPartnerPortalI18n";
import { FX_CURRENCY_ANALYTICS_SURFACES, FX_CURRENCY_SOURCE_COMPONENTS } from "../../constants/fxCurrencyAnalyticsConstants.js";
import CasaMapaSelect from "../casamap/chrome/CasaMapaSelect";
import DealerPortalLocaleControls from "../dealer/DealerPortalLocaleControls";

const LOCALE_LABELS = {
  "es-CO": "ES",
  "en-US": "EN",
  "ru-RU": "RU",
};

export default function AutoMapaPartnerPortalLocaleControls({ variant = "menu" }) {
  const idBase = useId();
  const { locale, setLocale, supportedLocales, t } = useLocale();
  const { displayCurrency, setDisplayCurrency, supportedCurrencies } = useCurrency();
  const manifestI18n = AUTOMAP_PARTNER_PORTAL_I18N_MANIFEST.i18n;
  const rollout = getI18nRollout({ i18n: manifestI18n });
  const isMenu = variant === "menu";
  const isSidebar = variant === "sidebar";

  const localeChoices =
    supportedLocales.length > 1 ? supportedLocales : manifestI18n.supportedLocales;

  const localeOptions = useMemo(
    () =>
      localeChoices.length > 1 && rollout.clientViewI18nEnabled !== false
        ? localeChoices.map((value) => ({
            value,
            label: LOCALE_LABELS[value] || localeToUrlSegment(value)?.toUpperCase() || value,
          }))
        : [],
    [localeChoices, rollout.clientViewI18nEnabled]
  );

  const currencyChoices =
    supportedCurrencies.length > 1 ? supportedCurrencies : manifestI18n.supportedCurrencies;

  const currencyOptions = useMemo(
    () =>
      currencyChoices.length > 1 && rollout.displayCurrencyEnabled !== false
        ? currencyChoices.map((value) => ({ value, label: value }))
        : [],
    [currencyChoices, rollout.displayCurrencyEnabled]
  );

  const showLanguage = localeOptions.length >= 2;
  const showCurrency = currencyOptions.length >= 2;

  if (!isMenu && !isSidebar) {
    return showLanguage || showCurrency ? <DealerPortalLocaleControls /> : null;
  }

  if (!showLanguage && !showCurrency) {
    return null;
  }

  const handleLocaleChange = (nextLocale) => {
    writeClientViewLocaleCookie(nextLocale);
    setLocale(nextLocale);
  };

  const handleCurrencyChange = (nextCurrency) => {
    writeClientViewCurrencyCookie(nextCurrency);
    setDisplayCurrency(nextCurrency, {
      sourceComponent: FX_CURRENCY_SOURCE_COMPONENTS.LOCALE_CONTROLS,
      surface: FX_CURRENCY_ANALYTICS_SURFACES.CURRENCY_CHANGE,
    });
  };

  const className = isSidebar
    ? "automap-admin-locale-tools automap-admin-locale-tools--sidebar"
    : "partner-portal-locale-tools partner-portal-locale-tools--menu";

  return (
    <div className={className}>
      {showLanguage ? (
        <CasaMapaSelect
          id={`${idBase}-lang`}
          label={t("common.language")}
          value={locale}
          options={localeOptions}
          onChange={handleLocaleChange}
        />
      ) : null}
      {showCurrency ? (
        <CasaMapaSelect
          id={`${idBase}-currency`}
          label={t("common.currency")}
          value={displayCurrency}
          options={currencyOptions}
          onChange={handleCurrencyChange}
        />
      ) : null}
    </div>
  );
}
